'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { buildSaleBundle, resolveSaleLabel } from '../../utils/facetLabels';
import { FlatFacetShell } from '../FlatFacetShell';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { SaleFacetProps } from './SaleFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

export const SaleFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: SaleFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const saleTitle = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-onsale-label',
    'Sale'
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.clear',
    'Clear'
  );

  const saleBundle = useMemo(
    () => buildSaleBundle(searchValuesMicrocopy),
    [searchValuesMicrocopy]
  );

  const controllers = useSearchControllers();
  const saleFacet = controllers.useSaleFacet();
  const {
    values,
    selectedIds,
    selectedCount,
    isReady,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({ facet: saleFacet, getValueId });

  const getLabel = useCallback(
    (value: string) => resolveSaleLabel(saleBundle, value),
    [saleBundle]
  );

  const treeItems = useMemo(
    () =>
      values.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.value),
        count: v.numberOfResults,
        disabled:
          !isReady || (v.numberOfResults === 0 && v.state !== 'selected'),
      })),
    [values, getLabel, isReady]
  );

  // Self-hide: when no sale is running the facet has nothing actionable to
  // offer, so it disappears entirely instead of rendering disabled rows.
  const hasVisibleValues = values.some(
    (v) => v.numberOfResults > 0 || v.state === 'selected'
  );
  if (!hasVisibleValues) return null;

  const isDark = variant === 'mobile';

  return (
    <FlatFacetShell
      title={saleTitle}
      clearLabel={clearLabel}
      selectedCount={selectedCount}
      onClear={handleClear}
      variant={variant}
      className={className}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!isReady}
    >
      <TreeAccordion
        idPrefix={getFacetTreeIdPrefix(variant)}
        items={treeItems}
        selectionMode="multiple"
        selectedIds={selectedIds}
        onSelectMany={handleSelectMany}
        isDarkTheme={isDark}
        size={variant === 'sidebar' ? 'default' : 'sm'}
        reserveExpandSpace={variant !== 'horizontal'}
      />
    </FlatFacetShell>
  );
};
